import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/comics';

export default function About() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩漫星球',
    description: '韩漫星球致力于提供免费韩国漫画下拉式阅读体验，了解我们的品牌故事、用户口碑和联系方式。',
    url: 'https://hanmanxingqiu.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫星球',
      url: 'https://hanmanxingqiu.com',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫星球',
    url: 'https://hanmanxingqiu.com',
    description: '韩漫星球提供免费漫画下拉式阅读和好看的韩国漫画大全，资源多样操作简单，无需付费即可探索各种故事。',
    sameAs: ['https://weibo.com/hanmanxingqiu', 'https://weixin.qq.com/hanmanxingqiu'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hanman.com',
      availableLanguage: 'Chinese',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <SEO
        title="关于我们_韩漫免费漫画下拉式阅读_韩漫星球"
        description="了解韩漫画星球品牌故事，我们致力于提供免费优质的韩国漫画下拉式阅读体验，资源多样操作简单，让您的休闲时间更充实。"
        keywords="韩漫星球,韩漫免费,漫画下拉式阅读,好看的韩国漫画大全,关于我们"
        canonicalUrl="https://hanmanxingqiu.com/about"
        jsonLd={{ ...jsonLd, ...orgJsonLd }}
      />

      <div className="bg-white">
        <Navbar />
      </div>

      <main>
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto pt-24 pb-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              关于我们
            </h1>
            <p className="text-sm text-foreground-500">了解韩漫星球的故事与使命</p>
          </div>

          {/* Tab Switcher */}
          <div className="flex items-center bg-background-100 rounded-full p-1 mb-10 max-w-md">
            {[
              { key: 'intro', label: '品牌介绍' },
              { key: 'reputation', label: '用户口碑' },
              { key: 'contact', label: '联系方式' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${activeTab === tab.key ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Brand Introduction */}
          {activeTab === 'intro' && (
            <article className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
              <div className="max-w-none">
                <div className="mb-10">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20reading%20website%20illustration%20with%20cozy%20reading%20atmosphere%2C%20warm%20lighting%2C%20bookshelves%20with%20colorful%20comic%20books%2C%20artistic%20watercolor%20style%2C%20soft%20cream%20and%20coral%20tones%2C%20inviting%20mood&width=1200&height=500&seq=about-hero&orientation=landscape"
                    alt="韩漫星球品牌形象"
                    title="韩漫星球 - 韩漫免费漫画下拉式阅读平台"
                    className="w-full rounded-lg object-cover h-48 md:h-64"
                  />
                </div>

                <h2 className="text-xl font-bold text-foreground-950 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩漫星球诞生于2024年，由一群热爱韩国漫画的资深读者共同创立。我们深知漫画爱好者对优质阅读体验的渴望，因此立志打造一个<strong>免费、便捷、资源丰富</strong>的韩漫阅读平台。
                  </p>
                  <p>
                    在韩漫星球，您可以找到最全的<strong>韩国漫画大全</strong>，从热门恋爱漫画到热血动作漫画，从悬疑推理到日常治愈，各类题材应有尽有。我们的团队每日精心筛选优质内容，确保每一位读者都能在这里找到自己喜欢的作品。
                  </p>
                  <p>
                    我们创新的<strong>下拉式阅读</strong>模式，让漫画阅读体验更加流畅自然。无需翻页，只需轻轻滑动，即可沉浸在精彩的漫画世界中。这种设计灵感来源于韩国主流漫画平台的阅读方式，经过我们的优化后，更加适合中文读者的使用习惯。
                  </p>

                  <h3 className="text-lg font-semibold text-foreground-950 mt-8 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                    我们的使命
                  </h3>
                  <p>
                    韩漫星球的使命是让每一位漫画爱好者都能够<strong>轻松、免费地享受高品质的漫画阅读体验</strong>。我们相信，好的故事不应该被付费墙阻挡，每个人都应该有探索不同世界的权利。通过持续不断的内容更新和技术优化，我们致力于成为中文互联网上最受欢迎的韩漫阅读平台。
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-10">
                  {[
                    { num: '5000+', label: '漫画资源', icon: 'ri-book-2-line' },
                    { num: '200万+', label: '月活用户', icon: 'ri-user-heart-line' },
                    { num: '99.8%', label: '用户好评率', icon: 'ri-thumb-up-line' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center p-5 bg-background-50 rounded-xl">
                      <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-rose-100 flex items-center justify-center">
                        <i className={`${stat.icon} text-rose-500 text-lg`} />
                      </div>
                      <p className="text-2xl font-bold text-foreground-950 mb-1">{stat.num}</p>
                      <p className="text-xs text-foreground-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          )}

          {/* Reputation */}
          {activeTab === 'reputation' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
                <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  用户真实评价
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {testimonials.map((t) => (
                    <div key={t.id} className="bg-background-50 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                        <div>
                          <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                          <div className="flex gap-0.5 mt-0.5">
                            {Array.from({ length: t.rating }).map((_, i) => (
                              <i key={i} className="ri-star-fill text-yellow-400 text-xs" />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
                <h3 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  平台优势
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-shield-check-line', title: '安全可靠', desc: '严格筛选内容来源，确保所有漫画资源安全无毒，让您放心阅读。' },
                    { icon: 'ri-speed-up-line', title: '加载快速', desc: '采用先进的CDN加速技术，漫画页面加载迅速，阅读不卡顿。' },
                    { icon: 'ri-palette-line', title: '画质高清', desc: '所有漫画均为高清画质，细节清晰可见，还原最真实的手绘质感。' },
                    { icon: 'ri-global-line', title: '多端适配', desc: '完美适配电脑、平板和手机，随时随地享受漫画阅读的乐趣。' },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-3 p-4 bg-background-50 rounded-lg">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center">
                        <i className={`${item.icon} text-rose-500`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-950 mb-1">{item.title}</p>
                        <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Contact */}
          {activeTab === 'contact' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
                <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  联系我们
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
                  {[
                    { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@hanman.com', desc: '工作日24小时内回复' },
                    { icon: 'ri-wechat-line', title: '微信公众号', value: '韩漫星球', desc: '获取最新漫画资讯' },
                    { icon: 'ri-customer-service-2-line', title: '在线客服', value: '工作日 9:00-18:00', desc: '点击右下角联系客服' },
                  ].map((item) => (
                    <div key={item.title} className="text-center p-5 bg-background-50 rounded-xl">
                      <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-rose-100 flex items-center justify-center">
                        <i className={`${item.icon} text-rose-500 text-lg`} />
                      </div>
                      <p className="text-sm font-semibold text-foreground-950 mb-1">{item.title}</p>
                      <p className="text-sm text-foreground-700 mb-1">{item.value}</p>
                      <p className="text-xs text-foreground-400">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <h3 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  意见反馈
                </h3>

                <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">反馈内容</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-100 transition-all resize-none"
                      placeholder="请描述您的问题或建议..."
                    />
                  </div>
                  <button
                    type="submit"
                    className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap"
                  >
                    提交反馈
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}